import { Accordion, AccordionDetails, AccordionSummary, Box, List, Stack, Typography } from '@mui/material';
import React, { type JSX } from 'react';
import { EmptyState, InfoListItem } from '@brightlayer-ui/react-components';
import Description from '@mui/icons-material/Description';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTypedSelector } from '@fiji/common/src/app/store';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import moment from 'moment';
import { Loader } from 'components';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import {
    useGetDocumentsByFilterMutation,
    useGetDocumentTypesQuery,
} from '@fiji/common/src/features/documents/documentApi';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { useNewCustomTranslations, useRBAC } from 'hooks';
import { DOCUMENTS_TEST_IDS, idOf } from '@fiji/common/src/testing/testId';
import { DocumentReportList } from './DocumentReportList';
import { toSafeHttpUrl } from 'utils/security';
import { DocumentDownloadButton } from './DocumentDownloadButton';

export const Document = (): JSX.Element => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    useNewCustomTranslations('DASHBOARD_MANAGEMENT');
    const { deviceId, groupId } = useParams();
    const currentRealmName = useTypedSelector((state) => state.common.selectedRealm);
    const theme: any = useTheme();
    const currentOrg = useTypedSelector(selectedOrg);
    const [expandedNodes, setExpandedNodes] = React.useState<any>([]);
    const [documentsData, setDocumentsData] = React.useState<any>({});
    const rolePermissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(rolePermissions);
    const canDownloadDocument = hasPermission('download-documents_view-devices');

    const {
        data: documentTypeData,
        isLoading: documentFetching,
        isFetching: documentLoading,
        isSuccess: documentTypesLoaded,
    } = useGetDocumentTypesQuery(
        {
            ...(deviceId && { deviceIds: [deviceId] }),
            ...(groupId && groupId !== currentOrg?.id && { groupIds: [groupId] }),
        },
        {
            refetchOnMountOrArgChange: true,
        }
    );

    const [getDocumentsByFilter] = useGetDocumentsByFilterMutation();
    const emptyState =
        documentTypesLoaded && !documentFetching && !documentLoading && documentTypeData?.data?.length === 0 ? (
            <Box sx={{ minHeight: 240, display: 'grid', placeItems: 'center', bgcolor: 'background.paper' }}>
                <EmptyState
                    icon={<Description fontSize="inherit" className="height-76 width-76 color-icon" />}
                    title={
                        deviceId
                            ? t('DASHBOARD_MANAGEMENT:NO_DOCUMENTS_AVAILABLE')
                            : t('DASHBOARD_MANAGEMENT:NO_DOCUMENTS_FOUND')
                    }
                />
            </Box>
        ) : null;

    const getAllDocumentsData = (documentResponse: any): any => {
        const allDocumentsData: any = [];
        documentResponse?.data?.data?.records?.map((modelDocuments: any) => {
            modelDocuments.documentsData?.map((docData: any) => {
                allDocumentsData.push(docData?.documents);
            });
        });
        return allDocumentsData?.flat?.() ?? [];
    };

    React.useEffect(() => {
        if (currentOrg?.id && expandedNodes?.length > 0) {
            expandedNodes.map(async (node: string) => {
                if (!documentsData[node]) {
                    const documentResponse: any = await getDocumentsByFilter({
                        page: 0,
                        size: 6,
                        filters: {
                            ...(deviceId && { deviceIds: [deviceId] }),
                            ...(groupId && groupId !== currentOrg?.id && { groupIds: [groupId] }),
                            documentTypes: [node],
                        },
                    });

                    setDocumentsData((prev: any) => ({
                        ...prev,
                        [node]: {
                            data: getAllDocumentsData(documentResponse),
                            total: documentResponse?.data?.data?.total,
                        },
                    }));
                }
            });
        }
    }, [expandedNodes]);

    const handleExpandCollapseNode = (categoryId: string): void => {
        const expandedNodesClone = JSON.parse(JSON.stringify(expandedNodes));
        const nodeIndex = expandedNodesClone.findIndex((node: string) => node === categoryId);
        if (nodeIndex !== -1) {
            expandedNodesClone.splice(nodeIndex, 1);
        } else {
            expandedNodesClone.push(categoryId);
        }
        setExpandedNodes(expandedNodesClone);
    };

    return (
        <Stack p={2}>
            {!deviceId && <DocumentReportList groupId={groupId ?? currentOrg?.id} embedded emptyState={emptyState} />}
            {deviceId && !documentFetching && !documentLoading && emptyState}
            {documentFetching || documentLoading ? (
                <Loader size={55} />
            ) : (
                <>
                    {documentTypeData?.data?.length > 0 && (
                        <>
                            {documentTypeData?.data?.map((documentType: any) => (
                                <Accordion
                                    key={documentType?.categoryId}
                                    expanded={expandedNodes?.includes(documentType?.categoryId)}
                                    onChange={() => handleExpandCollapseNode(documentType?.categoryId)}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon color="primary" />}
                                        aria-controls="panel1bh-content"
                                        id="panel1bh-header"
                                        className="border-bottom-1"
                                    >
                                        <Typography
                                            variant="body2"
                                            fontWeight={'600'}
                                            fontSize={'14px'}
                                            sx={{ color: theme.palette?.primary?.main }}
                                        >
                                            {documentType?.categoryName}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails className="padding-0">
                                        <List disablePadding>
                                            {documentsData?.[documentType?.categoryId] ? (
                                                <>
                                                    {documentsData?.[documentType?.categoryId]?.data?.map(
                                                        (document: any) => (
                                                            <InfoListItem
                                                                data-testid={DOCUMENTS_TEST_IDS.documentRow(
                                                                    idOf(document, 'fileId') ??
                                                                        idOf(document, 'id') ??
                                                                        idOf(document, 'name')
                                                                )}
                                                                onClick={(): void => {
                                                                    const safeUrl = toSafeHttpUrl(document?.url);
                                                                    if (safeUrl) {
                                                                        window.open(
                                                                            safeUrl,
                                                                            '_blank',
                                                                            'noopener,noreferrer'
                                                                        );
                                                                    }
                                                                }}
                                                                key={document?.url}
                                                                divider="partial"
                                                                title={document?.displayName}
                                                                subtitle={document?.name}
                                                                chevron
                                                                hidePadding
                                                                leftComponent={
                                                                    <Stack>
                                                                        <Typography
                                                                            variant="subtitle2"
                                                                            fontSize={'14px'}
                                                                            fontWeight={'600'}
                                                                            data-testid="document-month"
                                                                        >
                                                                            {new Date(
                                                                                Number(document?.timestamp)
                                                                            ).toLocaleString('default', {
                                                                                month: 'short',
                                                                            })}
                                                                        </Typography>
                                                                        <Typography
                                                                            variant="body2"
                                                                            fontSize={'12px'}
                                                                            letterSpacing={'1px'}
                                                                            textTransform={'uppercase'}
                                                                            data-testid="document-year"
                                                                        >
                                                                            {new Date(
                                                                                Number(document?.timestamp)
                                                                            )?.getFullYear()}
                                                                        </Typography>
                                                                    </Stack>
                                                                }
                                                                rightComponent={
                                                                    <>
                                                                        {document?.deleteSchedule && (
                                                                            <Typography
                                                                                variant="body2"
                                                                                fontSize={'14px'}
                                                                            >
                                                                                This file is scheduled for deletion on{' '}
                                                                                {moment(
                                                                                    new Date(document?.deleteSchedule)
                                                                                ).format('LL')}
                                                                            </Typography>
                                                                        )}
                                                                        {canDownloadDocument && (
                                                                            <DocumentDownloadButton
                                                                                testId={DOCUMENTS_TEST_IDS.downloadDocument(
                                                                                    idOf(document, 'fileId') ??
                                                                                        idOf(document, 'id') ??
                                                                                        idOf(document, 'name')
                                                                                )}
                                                                                url={document?.url}
                                                                                fileName={document?.name}
                                                                            />
                                                                        )}
                                                                    </>
                                                                }
                                                            />
                                                        )
                                                    )}
                                                    {Math.ceil(
                                                        documentsData?.[documentType?.categoryId]?.data?.length
                                                    ) < documentsData?.[documentType?.categoryId]?.total && (
                                                        <InfoListItem
                                                            key="showMore"
                                                            data-testid="infoListItem"
                                                            sx={{ color: theme.palette?.primary?.main }}
                                                            data-cy={'list-content'}
                                                            title={
                                                                <Typography color="primary" sx={{ fontWeight: 'bold' }}>
                                                                    {`View ${
                                                                        documentsData?.[documentType?.categoryId]
                                                                            ?.total -
                                                                        documentsData?.[documentType?.categoryId]?.data
                                                                            ?.length
                                                                    } More...`}
                                                                </Typography>
                                                            }
                                                            hidePadding
                                                            onClick={(): void => {
                                                                navigate(
                                                                    `/${currentRealmName}/document-list/${
                                                                        deviceId ? 'device' : 'group'
                                                                    }/${deviceId ?? groupId}/${
                                                                        documentType?.categoryId
                                                                    }`,
                                                                    {
                                                                        state: {
                                                                            categoryName: documentType?.categoryName,
                                                                        },
                                                                    }
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                </>
                                            ) : (
                                                <Box sx={{ height: 50 }}>
                                                    <Loader size={36} />
                                                </Box>
                                            )}
                                        </List>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </>
                    )}
                </>
            )}
        </Stack>
    );
};
